import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bug, CheckCircle, Clock, Wrench, RefreshCw, ChevronDown, Upload } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { formatDate, formatDateTime } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_CONFIG = {
  OPEN:       { label: 'Open',       color: '#ef4444', bg: '#ef444420' },
  WORKING:    { label: 'Working',    color: '#f59e0b', bg: '#f59e0b20' },
  FIXED:      { label: 'Fixed',      color: '#6366F1', bg: '#6366F120' },
  QA_TESTING: { label: 'QA Testing', color: '#8b5cf6', bg: '#8b5cf620' },
  VERIFIED:   { label: 'Verified',   color: '#16C784', bg: '#16C78420' },
  RESOLVED:   { label: 'Verified',   color: '#16C784', bg: '#16C78420' },
  REOPENED:   { label: 'Reopened',   color: '#ef4444', bg: '#ef444420' },
};

const STATUS_OPTIONS = ['OPEN', 'WORKING', 'FIXED', 'QA_TESTING', 'VERIFIED', 'REOPENED'];
const AUTHOR_OPTIONS = ['TEAM', 'QA', 'USER'];

function BugReportsPage() {
  const { colors } = useTheme();
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [updateDrafts, setUpdateDrafts] = useState({});

  const getDraft = (bugId) => (
    updateDrafts[bugId] || {
      author_type: 'TEAM',
      status: '',
      comment: '',
      screenshot: null,
      screenshotName: '',
      submitting: false,
    }
  );

  const setDraft = (bugId, patch) => {
    setUpdateDrafts(prev => ({ ...prev, [bugId]: { ...getDraft(bugId), ...patch } }));
  };

  const fetchBugs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/bugs`);
      const normalized = (res.data || []).map((bug) => {
        let updates = bug.updates;
        let screenshotUrls = bug.screenshot_urls;

        if (typeof screenshotUrls === 'string') {
          try {
            screenshotUrls = JSON.parse(screenshotUrls);
          } catch {
            screenshotUrls = [];
          }
        }

        if (!Array.isArray(screenshotUrls)) screenshotUrls = [];
        if (screenshotUrls.length === 0 && bug.screenshot) screenshotUrls = [bug.screenshot];

        if (typeof updates === 'string') {
          try {
            updates = JSON.parse(updates);
          } catch {
            updates = [];
          }
        }
        if (!Array.isArray(updates)) updates = [];
        return { ...bug, updates, screenshot_urls: screenshotUrls };
      });

      setBugs(normalized);
    } catch (e) {
      console.error('Failed to load bugs', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBugs(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API_URL}/bugs/${id}`, { status });
      await fetchBugs();
    } catch (e) {
      alert('Failed to update status');
    }
  };

  const handleUpdateScreenshot = (bugId, file) => {
    if (!file) {
      setDraft(bugId, { screenshot: null, screenshotName: '' });
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => alert('Could not read image.');
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => alert('Could not process image.');
      img.onload = () => {
        const MAX = 1280;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        setDraft(bugId, { screenshot: canvas.toDataURL('image/jpeg', 0.8), screenshotName: file.name });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submitUpdate = async (bugId) => {
    const draft = getDraft(bugId);
    const payload = {
      author_type: draft.author_type,
      comment: draft.comment,
      screenshot: draft.screenshot,
      status: draft.status || null,
    };

    if (!payload.comment?.trim() && !payload.screenshot && !payload.status) {
      alert('Add comment, screenshot, or status change.');
      return;
    }

    try {
      setDraft(bugId, { submitting: true });
      await axios.post(`${API_URL}/bugs/${bugId}/updates`, payload);
      setDraft(bugId, {
        submitting: false,
        comment: '',
        screenshot: null,
        screenshotName: '',
        status: '',
      });
      await fetchBugs();
    } catch (e) {
      alert('Failed to post update');
      setDraft(bugId, { submitting: false });
    }
  };

  const filtered = filter === 'ALL' ? bugs : bugs.filter(b => b.status === filter);
  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s] = bugs.filter(b => b.status === s).length;
    return acc;
  }, { ALL: bugs.length });

  const filters = ['ALL', ...STATUS_OPTIONS];

  return (
    <div className="space-y-3 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold" style={{ color: colors.primaryText }}>Bug Reports</h1>
          <p className="text-xs" style={{ color: colors.secondaryText }}>{bugs.length} total · {counts.OPEN || 0} open</p>
        </div>
        <button onClick={fetchBugs} className="app-card p-2 rounded-lg" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
          <RefreshCw className="w-4 h-4" style={{ color: colors.secondaryText }} />
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className="px-2.5 py-1 rounded-lg text-xs font-medium"
            style={{
              backgroundColor: filter === f ? colors.accent : colors.cardBg,
              color: filter === f ? '#fff' : colors.secondaryText,
              borderWidth: '1px',
              borderColor: filter === f ? colors.accent : colors.border,
            }}
          >
            {f} ({counts[f] ?? 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: colors.inputBg }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <Bug className="w-10 h-10 mx-auto mb-3 opacity-30" style={{ color: colors.secondaryText }} />
          <p className="text-sm" style={{ color: colors.secondaryText }}>No {filter !== 'ALL' ? filter.toLowerCase() : ''} bugs</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(bug => {
            const cfg = STATUS_CONFIG[bug.status] || STATUS_CONFIG.OPEN;
            const isExpanded = expandedId === bug.id;
            return (
              <div key={bug.id} className="app-card rounded-xl border overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, borderColor: isExpanded ? colors.accent + '60' : colors.border }}>
                {/* Header row */}
                <div className="p-3 flex items-start gap-3 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : bug.id)}>
                  <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: cfg.bg }}>
                    <Bug className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium leading-snug" style={{ color: colors.primaryText }}>
                      {bug.description.length > 80 ? bug.description.slice(0, 80) + '…' : bug.description}
                    </p>
                    <p className="text-[13px] mt-0.5" style={{ color: colors.secondaryText }}>
                      #{bug.id} · {bug.full_name || bug.email || 'Unknown'} · {formatDate(bug.created_at)}
                    </p>
                  </div>
                  {bug.screenshot_urls?.[0] && (
                    <div className="w-14 h-14 rounded-md border overflow-hidden flex items-center justify-center flex-shrink-0" style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}>
                      <img
                        src={bug.screenshot_urls[0]}
                        alt={`Bug ${bug.id} thumbnail`}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[13px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                    <ChevronDown className="w-3.5 h-3.5" style={{ color: colors.secondaryText, transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                  </div>
                </div>

                {/* Expanded */}
                {isExpanded && (
                  <div className="px-3 pb-3 border-t space-y-3" style={{ borderColor: colors.border }}>
                    <p className="text-sm pt-3 leading-relaxed" style={{ color: colors.primaryText }}>{bug.description}</p>

                    {Array.isArray(bug.screenshot_urls) && bug.screenshot_urls.length > 0 && (
                      <div className="rounded-lg border p-2" style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {bug.screenshot_urls.map((shot, idx) => (
                            <img
                              key={`bug-shot-${bug.id}-${idx}`}
                              src={shot}
                              alt={`Bug ${bug.id} screenshot ${idx + 1}`}
                              className="w-full rounded-lg max-h-[60vh] object-contain border"
                              style={{ borderColor: colors.border }}
                              loading="lazy"
                            />
                          ))}
                        </div>
                        <p className="text-[14px] mt-2" style={{ color: colors.secondaryText }}>
                          {bug.screenshot_urls.length} screenshot{bug.screenshot_urls.length > 1 ? 's' : ''} for bug #{bug.id}
                        </p>
                      </div>
                    )}

                    {Array.isArray(bug.updates) && bug.updates.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold" style={{ color: colors.secondaryText }}>Timeline</p>
                        <div className="space-y-2">
                          {bug.updates.map(u => (
                            <div key={u.id} className="rounded-lg border p-2" style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}>
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[14px] font-semibold" style={{ color: colors.primaryText }}>
                                  {(u.author_type || 'TEAM').toUpperCase()} update
                                </p>
                                <p className="text-[13px]" style={{ color: colors.secondaryText }}>
                                  {formatDateTime(u.created_at)}
                                </p>
                              </div>

                              {(u.from_status || u.to_status) && (
                                <p className="text-[14px] mt-1" style={{ color: colors.secondaryText }}>
                                  {u.from_status || bug.status} → {u.to_status || bug.status}
                                </p>
                              )}

                              {u.comment && (
                                <p className="text-xs mt-1" style={{ color: colors.primaryText }}>{u.comment}</p>
                              )}

                              {u.screenshot && (
                                <img
                                  src={u.screenshot}
                                  alt={`Bug ${bug.id} update screenshot`}
                                  className="mt-2 w-full rounded-md max-h-52 object-contain border"
                                  style={{ borderColor: colors.border }}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Update form (TEAM / QA / USER) */}
                    <div className="rounded-lg border p-3 space-y-2" style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}>
                      <p className="text-xs font-semibold" style={{ color: colors.secondaryText }}>
                        Add update (fix done / QA check / user re-open)
                      </p>

                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={getDraft(bug.id).author_type}
                          onChange={(e) => setDraft(bug.id, { author_type: e.target.value })}
                          className="px-2 py-2 rounded-md text-xs"
                          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText, border: `1px solid ${colors.border}` }}
                        >
                          {AUTHOR_OPTIONS.map(a => <option key={a} value={a} style={{ background: '#1a1a2e', color: '#e2e8f0' }}>{a}</option>)}
                        </select>

                        <select
                          value={getDraft(bug.id).status}
                          onChange={(e) => setDraft(bug.id, { status: e.target.value })}
                          className="px-2 py-2 rounded-md text-xs"
                          style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText, border: `1px solid ${colors.border}` }}
                        >
                          <option value="" style={{ background: '#1a1a2e', color: '#e2e8f0' }}>No status change</option>
                          {STATUS_OPTIONS.map(s => <option key={s} value={s} style={{ background: '#1a1a2e', color: '#e2e8f0' }}>{s}</option>)}
                        </select>
                      </div>

                      <textarea
                        rows={2}
                        value={getDraft(bug.id).comment}
                        onChange={(e) => setDraft(bug.id, { comment: e.target.value })}
                        placeholder="Comment: what was fixed, QA notes, or user feedback"
                        className="w-full px-2 py-2 rounded-md text-xs resize-none"
                        style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)`, color: colors.primaryText, border: `1px solid ${colors.border}` }}
                      />

                      <div className="flex items-center gap-2">
                        <label className="px-2 py-1.5 rounded-md text-[14px] cursor-pointer border" style={{ borderColor: colors.border, color: colors.secondaryText }}>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleUpdateScreenshot(bug.id, e.target.files?.[0])}
                          />
                          <span className="inline-flex items-center gap-1">
                            <Upload className="w-3.5 h-3.5" /> Attach screenshot
                          </span>
                        </label>
                        {getDraft(bug.id).screenshotName && (
                          <span className="text-[14px] truncate" style={{ color: colors.secondaryText }}>
                            {getDraft(bug.id).screenshotName}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => submitUpdate(bug.id)}
                        disabled={getDraft(bug.id).submitting}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold"
                        style={{ backgroundColor: colors.accent, color: '#fff', opacity: getDraft(bug.id).submitting ? 0.6 : 1 }}
                      >
                        {getDraft(bug.id).submitting ? 'Saving...' : 'Post Update'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BugReportsPage;
